import { useEffect, useState } from 'react';
import { isEmbeddedInIframe } from 'utils/utils';

export interface VisibleViewport {
	/** px from the top of the iframe document where the parent's visible fold begins. */
	visibleTop: number;
	/**
	 * Height of the on-screen part of the iframe: the host's `visibleHeight` when it
	 * sends one, else its `viewportHeight`, which is the whole window below the header
	 * and ignores both iframe edges; with a host that sends only `viewportHeight` the
	 * band is overstated by the iframe top's distance below the header plus the window
	 * bottom's distance below the iframe bottom (each when positive).
	 */
	viewportHeight: number;
}

/**
 * Resolves the parent page's visible viewport band when running inside the
 * Punchup iframe.
 *
 * The iframe is sized to its full document height (App.tsx posts
 * `document.body.scrollHeight`), so inside the iframe `window.innerHeight`
 * equals the whole document — NOT the visible area. Anything that clamps to the
 * fold (overlays, popovers, flip-up menus) must use the band the parent reports
 * via the `parent-scroll` protocol (`TixologiIframe.tsx` posts it on mount,
 * scroll, resize, and in answer to `request-viewport-offset`).
 *
 * Returns `null` when standalone (not embedded) or before the first
 * `parent-scroll` arrives — callers then fall back to `window.innerHeight`,
 * which is correct outside the iframe.
 *
 * @param enabled gate the listener so idle consumers (e.g. a closed row menu)
 *   don't subscribe to every parent scroll. Defaults to `true`.
 */
export const useVisibleViewport = (enabled = true): VisibleViewport | null => {
	const active = enabled && isEmbeddedInIframe();
	const [viewport, setViewport] = useState<VisibleViewport | null>(null);

	useEffect(() => {
		if (!active) return;
		const requestViewportOffset = () => window.parent.postMessage({ action: 'request-viewport-offset' }, '*');
		// Prime the first value before any scroll event fires.
		requestViewportOffset();
		// `visibleHeight` depends on the iframe's bottom edge, which moves when the host resizes the element
		// (after a height sync) without re-broadcasting; the iframe's own window resizes with it, so ask again.
		window.addEventListener('resize', requestViewportOffset);
		return () => window.removeEventListener('resize', requestViewportOffset);
	}, [active]);

	useEffect(() => {
		if (!active) return;
		const onMessage = (e: MessageEvent) => {
			if (e.data?.action !== 'parent-scroll') return;
			// Type-safe per-field read off the untyped cross-frame payload, preserving the
			// documented fallback (visibleTop 0 / window.innerHeight) when a field is absent.
			// `visibleHeight` is the exact band (on phones the host's fixed bottom navigation bar
			// overlays its bottom); hosts that predate it send only `viewportHeight`.
			const payload = e.data as { visibleTop?: unknown; viewportHeight?: unknown; visibleHeight?: unknown };
			let viewportHeight = window.innerHeight;
			if (typeof payload.visibleHeight === 'number') viewportHeight = payload.visibleHeight;
			else if (typeof payload.viewportHeight === 'number') viewportHeight = payload.viewportHeight;
			setViewport({
				visibleTop: typeof payload.visibleTop === 'number' ? payload.visibleTop : 0,
				viewportHeight,
			});
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [active]);

	return viewport;
};
